import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import serviceManagementService, { Service, ServicePrice } from '../services/serviceManagementService';
import { Pencil, Save, X, ArrowLeft } from 'lucide-react';
import './AdminServicePrices.scss';

interface EditingPrice {
  priceId: number;
  value: string;
}

const AdminServicePrices: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading } = useAuth();
  
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPrice, setEditingPrice] = useState<EditingPrice | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (!isLoading && (!isAuthenticated || user?.user_type !== 'admin')) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate, isLoading]);

  useEffect(() => {
    if (isAuthenticated && user?.user_type === 'admin') {
      fetchServices();
    }
  }, [isAuthenticated, user]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await serviceManagementService.getAllServices();
      if (Array.isArray(data)) {
        setServices(data);
      } else if ((data as any).data && Array.isArray((data as any).data)) {
        setServices((data as any).data);
      } else {
        setServices([]);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (price: ServicePrice) => {
    const priceValue = typeof price.price === 'string' ? price.price : price.price.toString();
    setEditingPrice({
      priceId: price.id,
      value: priceValue
    });
  };

  const handleCancelEdit = () => {
    setEditingPrice(null);
  };

  const handleSavePrice = async (priceId: number) => {
    if (!editingPrice || editingPrice.priceId !== priceId) return;

    const newPrice = parseFloat(editingPrice.value);
    if (isNaN(newPrice) || newPrice <= 0) {
      showToast('Įveskite teisingą kainą', 'warning');
      return;
    }

    try {
      setSaving(true);
      await serviceManagementService.updateServicePrice(priceId, newPrice);
      
      // Update local state
      setServices(prevServices => 
        prevServices.map(service => ({
          ...service,
          prices: service.prices.map(price => 
            price.id === priceId ? { ...price, price: newPrice } : price
          )
        }))
      );
      
      setEditingPrice(null);
      setSuccessMessage('Kaina sėkmingai atnaujinta!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating price:', error);
      showToast('Klaida atnaujinant kainą', 'error');
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (days: number): string => {
    if (days === 0) return 'Vienkartinė';
    if (days === 1) return '1 diena';
    if (days === 7) return '1 savaitė';
    if (days === 14) return '2 savaitės';
    if (days === 30) return '1 mėnuo';
    if (days === 90) return '3 mėnesiai';
    if (days === 365) return '1 metai';
    return `${days} dienų`;
  };

  const formatPrice = (price: number | string): string => {
    const priceNum = typeof price === 'string' ? parseFloat(price) : price;
    return priceNum.toFixed(2);
  };

  const getServiceTypeName = (type: string): string => {
    switch (type) {
      case 'cv_database_access': return 'CV duomenų bazės prieiga';
      case 'job_promotion': return 'Skelbimo iškėlimas';
      case 'additional_job_slot': return 'Papildoma skelbimo vieta';
      default: return type;
    }
  };

  if (isLoading || loading) {
    return (
      <div className="admin-service-prices">
        <div className="container">
          <p className="loading-text">Kraunama...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-service-prices">
      <div className="container">
        <div className="breadcrumbs">
          <span onClick={() => navigate('/dashboard/admin')} className="breadcrumb-link">
            <ArrowLeft size={16} /> Administratoriaus skydelis
          </span>
          <span className="breadcrumb-separator"> &gt; </span>
          <span className="breadcrumb-current">Paslaugų kainos</span>
        </div>

        <div className="page-header">
          <h1>Paslaugų kainų valdymas</h1>
          <p>Redaguokite paslaugų kainas paspausdami ant pieštuko ikonos</p>
        </div>

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <div className="services-list">
          {services.map((service) => (
            <div key={service.id} className="service-section">
              <div className="service-header">
                <h2>{service.name}</h2>
                <span className="service-type">{getServiceTypeName(service.service_type)}</span>
              </div>
              <p className="service-description">{service.description}</p>
              
              <div className="prices-table">
                <div className="table-header">
                  <div className="col-duration">Trukmė</div>
                  <div className="col-price">Kaina (EUR)</div>
                  <div className="col-actions">Veiksmai</div>
                </div>
                
                {service.prices.map((price) => (
                  <div key={price.id} className="table-row">
                    <div className="col-duration">
                      {formatDuration(price.duration_days)}
                    </div>
                    <div className="col-price">
                      {editingPrice?.priceId === price.id ? (
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          className="price-input"
                          value={editingPrice.value}
                          onChange={(e) => setEditingPrice({ ...editingPrice, value: e.target.value })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSavePrice(price.id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                      ) : (
                        <span className="price-value">{formatPrice(price.price)} €</span>
                      )}
                    </div>
                    <div className="col-actions">
                      {editingPrice?.priceId === price.id ? (
                        <>
                          <button 
                            className="btn-save"
                            onClick={() => handleSavePrice(price.id)}
                            disabled={saving}
                            title="Išsaugoti"
                          >
                            <Save size={16} />
                          </button>
                          <button 
                            className="btn-cancel"
                            onClick={handleCancelEdit}
                            disabled={saving}
                            title="Atšaukti"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <button 
                          className="btn-edit"
                          onClick={() => handleEditClick(price)}
                          title="Redaguoti kainą"
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminServicePrices;

